import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener('click', this.#closeContactOnOutsideClick);
    document.addEventListener('keydown', this.#closeContactOnEscape);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener('click', this.#closeContactOnOutsideClick);
    document.removeEventListener('keydown', this.#closeContactOnEscape);
  }

  /**
   * The Contact Us button's panel. Toggled on click/press only — never on hover, because on touch
   * the panel is the only route to the phone number and a hover-only affordance is unreachable
   * there. Open state lives on the button's aria-expanded, which the stylesheet keys off.
   * @returns {HTMLButtonElement | null}
   */
  get #contactToggle() {
    return this.querySelector('.header-actions__contact-btn');
  }

  /**
   * Toggle the contact panel. Bound declaratively via `on:click="/toggleContact"`.
   * @returns {void}
   */
  toggleContact = () => {
    const toggle = this.#contactToggle;

    if (!toggle) return;

    this.#setContactExpanded(toggle.getAttribute('aria-expanded') !== 'true');
  };

  /**
   * @param {boolean} isExpanded
   * @returns {void}
   */
  #setContactExpanded(isExpanded) {
    this.#contactToggle?.setAttribute('aria-expanded', String(isExpanded));
  }

  /** @param {MouseEvent} event */
  #closeContactOnOutsideClick = (event) => {
    const contact = this.querySelector('.header-actions__contact');

    if (!contact || !(event.target instanceof Node) || contact.contains(event.target)) return;

    this.#setContactExpanded(false);
  };

  /** @param {KeyboardEvent} event */
  #closeContactOnEscape = (event) => {
    if (event.key !== 'Escape') return;

    const toggle = this.#contactToggle;

    if (toggle?.getAttribute('aria-expanded') !== 'true') return;

    this.#setContactExpanded(false);
    toggle.focus();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
